import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  Icon,
  Badge,
  Progress,
  Tooltip,
  useColorModeValue,
  useBreakpointValue,
} from '@chakra-ui/react';
import { 
  FaWallet, 
  FaPlus, 
  FaMinus, 
  FaClock, 
  FaInfoCircle,
  FaChartLine 
} from 'react-icons/fa';

const WalletBalance = ({ walletSummary, onAddFunds, onPayout }) => {
  // Responsive values
  const cardSize = useBreakpointValue({ base: 'sm', md: 'md', lg: 'lg' });
  const fontSize = useBreakpointValue({ base: 'xl', md: '2xl', lg: '3xl' });
  const buttonSize = useBreakpointValue({ base: 'sm', md: 'md' });
  
  // Theme colors
  const cardBg = "secondary.100";
  const borderColor = 'dark.900';
  
  if (!walletSummary) {
    return (
      <Box
        w="100%"
        p={6}
        bg={cardBg}
        borderRadius="xl"
        border="1px solid"
        borderColor={borderColor}
        minH="200px"
      >
        <VStack justify="center" h="100%">
          <Text color="secondary.900">Loading wallet balance...</Text>
        </VStack>
      </Box>
    );
  }

  const {
    available_balance = 0,
    pending_earnings = 0,
    total_earned = 0,
    total_spent = 0,
    eligible_earnings_amount = 0,
    pending_earnings_count = 0
  } = walletSummary;

  const totalBalance = available_balance + pending_earnings;
  const canWithdraw = available_balance >= 10;

  return (
    <Box
      w="100%"
      p={6}
      bg={cardBg}
      borderRadius="xl"

      position="relative"
      overflow="hidden"
    >

      
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between">
          <HStack>
            <Icon as={FaWallet} boxSize={6} color="secondary.500" />
            <Text fontSize="lg" fontWeight="bold" color="primary.100">
              Wallet Balance
            </Text>
          </HStack>
          {pending_earnings_count > 0 && (
            <Tooltip label={`${pending_earnings_count} earnings pending 5-day clearance`}>
              <Badge colorScheme="yellow" variant="subtle" borderRadius="full">
                {pending_earnings_count} pending
              </Badge>
            </Tooltip>
          )}
        </HStack>

        {/* Main Balance */}
        <VStack spacing={2} align="center">
    
          <Text fontSize={fontSize} fontWeight="bold" color="dark.900">
            ${available_balance.toFixed(2)}
          </Text>
          
          {pending_earnings > 0 && (
            <HStack spacing={2}>
              <Icon as={FaClock} color="dark.900" boxSize={4} />
              <Text fontSize="sm" color="primary.500">
                +${pending_earnings.toFixed(2)} pending
              </Text>
              <Tooltip label="Earnings are held for 5 business days before becoming available">
                <Icon as={FaInfoCircle} color="secondary.900" boxSize={3} cursor="help" />
              </Tooltip>
            </HStack>
          )}
        </VStack>

        {/* Balance Breakdown */}
        {totalBalance > 0 && (
          <VStack spacing={3}>
            <Progress 
              value={(available_balance / totalBalance) * 100} 
              colorScheme="orange"
              size="sm"
              w="100%"
              borderRadius="full"
              bg="dark.900"
            />
            
            <HStack justify="space-between" w="100%" fontSize="xs" color="secondary.900">
              <Text>Available: ${available_balance.toFixed(2)}</Text>
              <Text>Pending: ${pending_earnings.toFixed(2)}</Text>
            </HStack>
          </VStack>
        )}

        {/* Quick Stats */}
        <HStack justify="space-around" pt={4} borderTop="1px solid" borderColor={borderColor}>
          <VStack spacing={1}>
            <Text fontSize="xs" color="primary.500" textTransform="uppercase">
              Total Earned
            </Text>
            <Text fontSize="sm" fontWeight="bold" color="secondary.500">
              ${total_earned.toFixed(2)}
            </Text>
          </VStack>
          
          <VStack spacing={1}>
            <Text fontSize="xs" color="primary.500" textTransform="uppercase">
              Total Spent
            </Text>
            <Text fontSize="sm" fontWeight="bold" color="primary.900">
              ${total_spent.toFixed(2)}
            </Text>
          </VStack>
          
          {eligible_earnings_amount > 0 && (
            <VStack spacing={1}>
              <Text fontSize="xs" color="primary.500" textTransform="uppercase">
                Ready to Transfer
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="secondary.500">
                ${eligible_earnings_amount.toFixed(2)}
              </Text>
            </VStack>
          )}
        </HStack>

        {/* Action Buttons */}
        <HStack spacing={3} pt={2}>
          <Button
            leftIcon={<FaPlus />}
            variant="solid"
            bg="secondary.500"
            color="dark.900"
            _hover={{ bg: "secondary.600" }}
            onClick={onAddFunds}
            flex={1}
            size={buttonSize}
          >
            Add Funds
          </Button>
          
          <Button
            leftIcon={<FaMinus />}
            variant="outline"
            borderColor="dark.900"
            color="primary.500"
            _hover={{ bg: "secondary.500", color: "dark.900" }}
            onClick={onPayout}
            isDisabled={!canWithdraw}
            flex={1}
            size={buttonSize}
          >
            {canWithdraw ? 'Withdraw' : 'Min $10'}
          </Button>
        </HStack>

        {/* Withdrawal Info */}
        {!canWithdraw && available_balance > 0 && (
          <Text fontSize="xs" color="secondary.900" textAlign="center">
            Minimum withdrawal amount is $10.00
          </Text>
        )}
      </VStack>
    </Box>
  );
};

export default WalletBalance;